import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { ConfirmModalComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import imageCompression from 'browser-image-compression';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faTrash, faUpload } from '@fortawesome/pro-light-svg-icons';
import { SafeUrlPipe } from './safe-url.pipe';

/** Pick, crop, and emit an image (base64); optionally patch a form control. */
@Component({
  selector: 'ct-lib-image-editor',
  templateUrl: './image-editor.component.html',
  styleUrls: ['./image-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SafeUrlPipe,
    ConfirmModalComponent,
    ImageCropperComponent,
    TranslocoPipe,
    FaIconComponent,
  ],
})
export class ImageEditorComponent {
  private readonly toastsService = inject(ToastsService);
  private readonly translateService = inject(TranslocoService);
  private readonly cropperModal =
    viewChild.required<ConfirmModalComponent>('cropperModal');

  protected readonly faTrash = faTrash;
  protected readonly faUpload = faUpload;

  readonly control = input(new UntypedFormControl(null, []));
  readonly submitAttempt = input(false);
  readonly ratio = input(1);
  readonly maintainRatio = input(true);
  readonly imageSrc = input<string | null>(null);
  /** Fallback when there is no `imageSrc` / crop preview. */
  readonly placeholder = input<string | undefined>(undefined);
  /** Avatar initials when no image is selected. */
  readonly initials = input<string | undefined>(undefined);
  readonly accept = input('image/png, image/jpeg');
  readonly small = input(false);
  readonly confirmed = output<string>();
  readonly deleted = output<void>();

  private readonly fileSizeLimit = 20_000_000;
  readonly inputFile = signal<File | null>(null);
  private readonly croppedImage = signal<string | null>(null);
  private readonly displaySrc = signal<string | null>(null);
  readonly inputId = `imageInput${Math.ceil(Math.random() * 9999).toString()}`;

  readonly imageForDisplay = computed((): string | null => {
    const candidate = [this.displaySrc(), this.imageSrc(), this.placeholder()].find(
      (value): value is string => typeof value === 'string' && value.trim().length > 0,
    );
    return candidate ?? null;
  });

  fileChangeEvent(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!this.isTypeAllowed(file.type)) {
      this.alertTranslated('ct-library.image-editor.wrong-type-toast', {
        fileName: file.name,
      });
      return;
    }
    void this.openCropper(file);
  }

  /**
   * Opens the cropper for a selected image. PNG skips compression to keep alpha.
   * @param file Selected image from the file input
   */
  private async openCropper(file: File): Promise<void> {
    const prepared =
      file.type === 'image/png'
        ? file
        : await imageCompression(file, { maxWidthOrHeight: 1000 });
    if (prepared.size >= this.fileSizeLimit) {
      this.alertTranslated('ct-library.file-input.file-size-exceeded', {
        fileName: file.name,
      });
      return;
    }
    this.inputFile.set(prepared);
    this.cropperModal().modal.open();
  }

  imageCropped(cropped: ImageCroppedEvent): void {
    this.croppedImage.set(cropped.base64 ?? null);
  }

  async confirmCrop(): Promise<void> {
    const src = this.croppedImage();
    if (src === null) return;
    this.displaySrc.set(src);
    this.confirmed.emit(src);

    const buffer = await (await fetch(src)).arrayBuffer();
    const file = new File([buffer], 'image.png', { type: 'image/png' });
    if (file.size >= this.fileSizeLimit) {
      this.alertTranslated('ct-library.file-input.file-size-exceeded', {
        fileName: file.name,
      });
      return;
    }
    this.control().patchValue(file);
  }

  confirmDelete(): void {
    this.control().patchValue(null);
    this.displaySrc.set(null);
    this.croppedImage.set(null);
    this.deleted.emit();
  }

  /**
   * @param type MIME type from the selected file
   * @returns Whether {@link accept} allows this type
   */
  private isTypeAllowed(type: string): boolean {
    const accept = this.accept();
    if (accept === '*') return true;
    const acceptedTypes = accept.split(/, ?/);
    if (acceptedTypes.includes('image/jpeg')) acceptedTypes.push('image/jpg');
    return acceptedTypes.includes(type);
  }

  /**
   * @param key Transloco key
   * @param params Optional interpolation params
   */
  private alertTranslated(key: string, params?: Record<string, string>): void {
    this.toastsService.alert(this.translateService.translate(key, params));
  }
}
